// src/views/menage/BandeauAgent.jsx — LA TÊTE DE « MON PLANNING », d'après la
// maquette NEST v677 : bande blanche, la date, « Bonjour Sofia », quatre tuiles
// (ménages · contrôle · à reprendre · changements). Chaque tuile ouvre sa liste
// en dessous. Vert = fini, aubergine = il en reste ; aucun bleu.
//
// La maquette ne dit pas comment un changement cesse d'être affiché : « Vu,
// merci » (le mot de sa feuille « Votre planning a changé ») acquitte en base.

import { useState } from 'react'
import { Brush, Eye, RotateCcw, CalendarDays, Navigation, ChevronDown, AlertTriangle } from 'lucide-react'
import { NEST, POLICE } from './palette-nest'

const ICONES = { men: Brush, ctl: Eye, rep: RotateCcw, chg: CalendarDays, itv: Navigation }
const TONS = {
  ok: { fond: NEST.vertPale, chiffre: NEST.vertFonce, texte: NEST.vertTexte },
  ab: { fond: NEST.aubPale, chiffre: NEST.aubTexte, texte: NEST.aub },
  gr: { fond: NEST.grisPale, chiffre: NEST.encre, texte: NEST.gris },
}

function Tuile({ tuile, ouverte, onOuvrir }) {
  const t = TONS[tuile.ton] || TONS.gr
  const Icone = ICONES[tuile.cle]
  const encre = ouverte ? '#fff' : t.chiffre
  return (
    <button type="button" data-testid={`agent-bloc-${tuile.cle}`} data-ton={tuile.ton} aria-expanded={ouverte}
      onClick={onOuvrir}
      style={{ background: ouverte ? NEST.vert : t.fond, borderRadius: 12, padding: '10px 14px', minHeight: 44, textAlign: 'left',
        border: `1px solid ${ouverte ? NEST.encre : 'transparent'}`, fontFamily: POLICE, cursor: 'pointer', display: 'block', width: '100%' }}>
      <span style={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ display: 'inline-flex', flex: 1 }}><Icone size={18} strokeWidth={2} color={encre} aria-hidden="true" /></span>
        <ChevronDown size={18} strokeWidth={2} color={encre} aria-hidden="true"
          style={{ transform: ouverte ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
      </span>
      <b style={{ display: 'block', fontSize: 22, fontWeight: 600, color: encre, lineHeight: 1.2 }}>{tuile.chiffre}</b>
      <span style={{ display: 'block', fontSize: 11, color: ouverte ? '#fff' : t.texte, lineHeight: 1.35 }}>{tuile.texte}</span>
    </button>
  )
}

/** Une ligne d'un panneau : vert fini, aubergine il en reste. */
function Ligne({ ligne, onAller }) {
  const fini = ligne.fini
  const Icone = ICONES[ligne.icone] || Brush
  return (
    <button type="button" data-testid={`agent-ligne-${ligne.cle}`} onClick={() => onAller(ligne.cle)}
      style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 48, padding: '9px 12px', textAlign: 'left',
        borderRadius: 10, border: 'none', background: fini ? NEST.vertLigne : NEST.aubPale, fontFamily: POLICE, cursor: 'pointer' }}>
      <span style={{ width: 28, height: 28, borderRadius: 7, background: fini ? NEST.vert : NEST.aub, flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icone size={16} strokeWidth={2} color="#fff" aria-hidden="true" />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <b style={{ display: 'block', fontSize: 13.5, fontWeight: 700, color: NEST.encre }}>{ligne.titre}</b>
        {ligne.sousTitre && <span style={{ display: 'block', fontSize: 12, color: NEST.grisFonce }}>{ligne.sousTitre}</span>}
      </span>
      <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 6, padding: '2px 8px', whiteSpace: 'nowrap',
        background: fini ? NEST.vert : NEST.aub, color: '#fff' }}>{ligne.etiquette}</span>
      <i aria-hidden="true" style={{ color: NEST.grisFonce, fontStyle: 'normal', fontSize: 16 }}>›</i>
    </button>
  )
}

export default function BandeauAgent({ date, prenom, tuiles, lignes, changements, onAller, onVu, vuEnCours }) {
  const [ouverte, setOuverte] = useState(null)
  const cleOuverte = tuiles.some((t) => t.cle === ouverte) ? ouverte : null
  return (
    <div data-testid="agent-bandeau" style={{ background: '#fff', borderBottom: `1px solid ${NEST.bordCarte}`, padding: '18px 24px', fontFamily: POLICE, color: NEST.encre }}>
      <p data-testid="agent-date" style={{ margin: 0, fontSize: 11.5, color: NEST.gris }}>{date}</p>
      <h2 data-testid="agent-bonjour" style={{ margin: '2px 0 0', fontSize: 19, fontWeight: 600 }}>{prenom ? `Bonjour ${prenom}` : 'Bonjour'}</h2>
      <div data-testid="agent-tuiles" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginTop: 14 }}>
        {tuiles.map((t) => (
          <Tuile key={t.cle} tuile={t} ouverte={cleOuverte === t.cle}
            onOuvrir={() => setOuverte((o) => (o === t.cle ? null : t.cle))} />
        ))}
      </div>
      {cleOuverte && (cleOuverte === 'chg' || (lignes[cleOuverte] || []).length > 0) && (
        <div data-testid={`agent-panneau-${cleOuverte}`} style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${NEST.bordCarte}`,
          display: 'flex', flexDirection: 'column', gap: 6 }}>
          {cleOuverte === 'chg'
            ? (<>
              {changements.map((c) => (
                <div key={c.id} data-testid={`agent-changement-${c.id}`} style={{ display: 'flex', alignItems: 'center', gap: 9, minHeight: 38, fontSize: 13 }}>
                  <span style={{ width: 26, height: 26, borderRadius: 7, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    background: c.alerte ? NEST.aubPale : NEST.vertPale }}>
                    {c.alerte
                      ? <AlertTriangle size={15} strokeWidth={2} color={NEST.aubTexte} aria-hidden="true" />
                      : <CalendarDays size={15} strokeWidth={2} color={NEST.vertFonce} aria-hidden="true" />}
                  </span>
                  <span style={{ minWidth: 0 }}><b>{c.mot}</b> · {c.texte}</span>
                </div>
              ))}
              <button type="button" data-testid="agent-changements-vus" onClick={onVu} disabled={vuEnCours}
                style={{ alignSelf: 'flex-start', minHeight: 44, padding: '0 16px', borderRadius: 10, border: 'none', background: NEST.encre,
                  color: '#fff', fontFamily: POLICE, fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: vuEnCours ? .6 : 1 }}>
                Vu, merci
              </button>
            </>)
            : (lignes[cleOuverte] || []).map((l) => <Ligne key={l.cle} ligne={l} onAller={onAller} />)}
        </div>
      )}
    </div>
  )
}
